import React from 'react';

import {
  Box,
  Container,
  Link as MuiLink,
  Stack,
  Step,
  StepLabel,
  Stepper,
  styled,
  Typography
} from '@mui/material';
import _ from 'lodash';
import {
  authService,
  imageUtil,
  ResponsiveImage,
  routeDirectory
} from 'modules';

import { useNavigate } from 'react-router-dom';
import * as dataService from './dataService';



const imgsLogo = imageUtil.getUrlSet('logo', 'png');


const RootContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(10),
  display: 'flex',
  justifyContent: 'center',
  minHeight: `calc(100vh - ${theme.spacing(10)})`
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: 640,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(12)
}));

const StyledStep = styled(Step, {
  shouldForwardProp: prop => prop !== 'hideStep'
})<{
  hideStep: boolean;
}>(({ hideStep }) => ({
  minWidth: 100,
  opacity: hideStep ? 0 : 1,
  '&.Mui-completed .MuiSvgIcon-root': {
    color: '#528898'
  }
}));

const HeaderStack = styled(Stack)(() => ({
  marginBottom: 100
}));

const LogInBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2)
}));

const Link = styled(MuiLink)({
  cursor: 'pointer'
});



export interface IProps {
  stepperMode: 'none' | 'signUp' | 'userType' | 'profile' | 'payment' | 'complete';
  title?: string;
  subtitle?: React.ReactNode;

  children?: React.ReactNode | React.ReactNode[];
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery();

  const steps = React.useMemo(
    () => {
      if (queryTuple.loading) {
        return [];
      }

      return [
        {
          name: 'signUp',
          displayName: 'Sign Up',
          show: true
        },
        {
          name: 'userType',
          displayName: 'User Type',
          show: true
        },
        {
          name: 'profile',
          displayName: 'Profile',
          show: !queryTuple.data.allAccessEnabled
        },
        {
          name: 'payment',
          displayName: 'Payment',
          show: !queryTuple.data.allAccessEnabled
        }
      ];
    },
    [queryTuple]
  );

  const activeStepIndex = props.stepperMode === 'complete'
    ? steps.length
    : _.findIndex(steps, step => step.name === props.stepperMode);

  const navigate = useNavigate();



  async function handleClickLogInNow() {
    await authService.logOut();
    navigate(routeDirectory.authentication.login.path());
  }



  return (
    <RootContainer>
      <ContentContainer>
        <HeaderStack
          direction="row"
          justifyContent="space-between"
          alignItems='center'>
          <Box>
            <ResponsiveImage.FC urlSet={imgsLogo} />
          </Box>

          {activeStepIndex >= 0 && (
            <Stepper
              alternativeLabel
              activeStep={activeStepIndex}>
              {_.map(steps, step => (
                <StyledStep
                  hideStep={!step.show}
                  key={step.name}>
                  <StepLabel>
                    {step.displayName}
                  </StepLabel>
                </StyledStep>
              ))}
            </Stepper>
          )}
        </HeaderStack>

        <Stack spacing={7}>
          <Stack spacing={2}>
            <Typography variant="h1">
              {props.title}
            </Typography>

            {_.isString(props.subtitle) ? (
              <Typography>
                {props.subtitle}
              </Typography>) :
              props.subtitle
            }
          </Stack>

          <Box>
            {props.children}

            <LogInBox>
              {'Already have an account? '}

              <Link
                type="button"
                onClick={handleClickLogInNow}>
                Log in now!
              </Link>
            </LogInBox>
          </Box>
        </Stack>

        <div />
      </ContentContainer>
    </RootContainer>
  );
};
